import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getDebugSkills, setDebugSkillEnabled, type DebugSkill } from '../../app/debug-skills'
import { queryKeys } from '../../app/queries'
import { Card, CardTitle } from '../../components/card'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import { Toggle } from '../../components/toggle'
import { T } from '../../copy/text'

/**
 * 排班与自动处置。**不是** `/skills` 那屏 Agent 库。
 *
 * 数据来自会话门的 GET /api/v1/duty/skills（Control 转发 orchestrator /debug/skills）。登记了几条就显示几条，
 * 不编 16 张空卡。M7-04 多 AI 协同单独一张灰卡。
 */
export function DutyScreen() {
  const queryClient = useQueryClient()
  const skills = useQuery({
    queryKey: queryKeys.debugSkills,
    queryFn: getDebugSkills,
  })
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => setDebugSkillEnabled(id, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.debugSkills }),
  })

  if (skills.isPending) return <ScreenLoading shape="split" />
  if (skills.error) return <ScreenError error={skills.error} />

  const rows = skills.data?.skills ?? []
  const observe = rows.filter((row) => row.Kind !== 'act')
  const act = rows.filter((row) => row.Kind === 'act')

  return (
    <Page>
      <PageHeader
        title={<T id="duty.page.title" />}
        subtitle={<T id="duty.page.subtitle" />}
        className="mb-npc-modal"
      />

      <Card className="mb-npc-card" data-testid="duty-schedule">
        <CardTitle className="mb-npc-grid-sm block">
          <T id="duty.schedule.heading" />
        </CardTitle>
        {rows.length === 0 ? (
          <p className="m-0 text-npc-body-sm text-npc-ink-soft" data-testid="duty-schedule-empty">
            <T id="duty.schedule.empty" />
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-npc-grid p-0">
            {rows.map((row) => (
              <li key={row.ID} data-testid={`duty-row-${row.ID}`} className="flex flex-wrap items-center justify-between gap-npc-grid-sm">
                <div className="min-w-0">
                  <div className="text-npc-body-sm text-npc-ink-strong">{row.What}</div>
                  <div className="mt-[3px] text-npc-meta text-npc-ink-softer">
                    {row.Describe}
                    {' · '}
                    <T id="duty.schedule.source" />
                  </div>
                </div>
                <Toggle
                  id={`duty-toggle-${row.ID}`}
                  checked={row.Enabled}
                  disabled={toggle.isPending}
                  onCheckedChange={(on) => toggle.mutate({ id: row.ID, enabled: on })}
                  label={<T id={row.Enabled ? 'duty.toggle.on' : 'duty.toggle.off'} />}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <section className="grid gap-npc-grid sm:grid-cols-2" data-testid="duty-cards">
        <CardTitle className="sm:col-span-2">
          <T id="duty.cards.heading" />
        </CardTitle>
        {[...observe, ...act].map((row) => (
          <SkillCard key={row.ID} row={row} />
        ))}
        <Card tone="warm" data-testid="duty-collab-pending">
          <CardTitle className="mb-npc-grid-sm block">
            <T id="duty.collab.pending" />
          </CardTitle>
        </Card>
      </section>
    </Page>
  )
}

function SkillCard({ row }: { row: DebugSkill }) {
  return (
    <Card data-testid={`duty-card-${row.ID}`}>
      <CardTitle className="mb-npc-grid-sm block">{row.What}</CardTitle>
      <p className="m-0 text-npc-meta text-npc-ink-softer">
        <T id={row.Kind === 'act' ? 'duty.kind.act' : 'duty.kind.observe'} />
        
      </p>
    </Card>
  )
}
